/**
 * HiringAssessmentDetail — recruiter shortlist view for one assessment.
 *
 * Shows ranked candidates (cutoff-passed first, then higher score,
 * fewer violations, higher AI fit). Recruiter can generate AI fit
 * summaries on-demand or in batch, copy the public link, and adjust
 * cutoff / capture config.
 *
 * Feb 2026 · R12.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Copy, ExternalLink, Sparkles, AlertTriangle, CheckCircle2, XCircle, Users, Save, RefreshCw, Trophy, Ban, BarChart3, ListChecks, Download, Mail } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

const CAPTURE_FIELDS = [
  { key: "phone", label: "Phone" },
  { key: "linkedin_url", label: "LinkedIn" },
  { key: "resume_url", label: "Resume" },
  { key: "current_ctc", label: "CTC" },
  { key: "notice_period", label: "Notice" },
  { key: "years_experience", label: "Yrs exp" },
];

export default function HiringAssessmentDetail() {
  const { activeWorkspaceId } = useAuth();
  const { id: aid } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("shortlist");
  const [assessment, setAssessment] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [counts, setCounts] = useState({ total: 0, passed: 0, failed: 0, flagged: 0 });
  const [loading, setLoading] = useState(true);
  const [inspectId, setInspectId] = useState(null);
  const [rankBusy, setRankBusy] = useState(false);
  const [genOne, setGenOne] = useState({});

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/hiring/assessments/${aid}`),
        api.get(`/workspaces/${activeWorkspaceId}/hiring/assessments/${aid}/candidates`),
      ]);
      setAssessment(a.data);
      setCandidates(c.data.candidates || []);
      setCounts(c.data.counts || {});
    } catch (e) {
      toast.error(apiErrorMessage(e, "Load failed"));
      navigate("/app/lms/hiring");
    } finally { setLoading(false); }
  }, [activeWorkspaceId, aid, navigate]);
  useEffect(() => { load(); }, [load]);

  const copyLink = async () => {
    const url = `${window.location.origin}${assessment.public_path}`;
    try { await navigator.clipboard.writeText(url); toast.success("Public link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const generateOneFit = async (candidate) => {
    setGenOne(g => ({ ...g, [candidate.id]: true }));
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/hiring/candidates/${candidate.id}/fit-summary`);
      toast.success("AI summary generated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "AI summary failed")); }
    finally { setGenOne(g => ({ ...g, [candidate.id]: false })); }
  };

  const rankAll = async () => {
    setRankBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/hiring/assessments/${aid}/rank-all`);
      toast.success(`Queued ${data.queued} candidate${data.queued === 1 ? "" : "s"} for AI ranking`);
      // Poll for updates every 3s for 30s
      let attempts = 0;
      const iv = setInterval(async () => {
        attempts++;
        await load();
        if (attempts >= 10) { clearInterval(iv); setRankBusy(false); }
      }, 3000);
    } catch (e) { toast.error(apiErrorMessage(e, "Rank all failed")); setRankBusy(false); }
  };

  const closeAssessment = async () => {
    if (!window.confirm("Close this assessment? The public link will be revoked immediately.")) return;
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/hiring/assessments/${aid}`, { status: "closed" });
      toast.success("Assessment closed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Close failed")); }
  };

  const saveCutoff = async (newCutoff) => {
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/hiring/assessments/${aid}`, { cutoff_score: Number(newCutoff) });
      toast.success("Cutoff updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!assessment) return null;

  const inspect = candidates.find(c => c.id === inspectId);

  return (
    <div className="p-6 space-y-4" data-testid="hiring-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="rounded-none" onClick={() => navigate("/app/lms/hiring")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
      <PageHeader
        title={assessment.title}
        description={`${assessment.role_title} · ${assessment.seniority} · cutoff ${assessment.cutoff_score}%`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={copyLink} data-testid="hiring-detail-copy">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
            </Button>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => window.open(assessment.public_path, "_blank")} data-testid="hiring-detail-open">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview
            </Button>
            <Button variant="outline" size="sm" className="rounded-none" onClick={rankAll} disabled={rankBusy} data-testid="hiring-rank-all">
              {rankBusy ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              AI rank all
            </Button>
            {assessment.status !== "closed" && (
              <Button variant="outline" size="sm" className="rounded-none text-rose-600 border-rose-300" onClick={closeAssessment} data-testid="hiring-close">
                <Ban className="w-3.5 h-3.5 mr-1" /> Close
              </Button>
            )}
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<Users className="w-4 h-4 text-neutral-600" />} label="Candidates" value={counts.total || 0} />
        <Kpi icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" />} label="Above cutoff" value={counts.passed || 0} />
        <Kpi icon={<XCircle className="w-4 h-4 text-rose-500" />} label="Below cutoff" value={counts.failed || 0} />
        <Kpi icon={<AlertTriangle className="w-4 h-4 text-amber-500" />} label="Flagged" value={counts.flagged || 0} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b">
        {[
          ["shortlist", "Ranked shortlist"],
          ["all", "All attempts"],
          ["questions", "Questions"],
          ["analytics", "Analytics"],
          ["settings", "Settings"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm border-b-2 ${tab === k ? "border-emerald-500 text-emerald-600" : "border-transparent text-neutral-600 hover:text-neutral-900"}`} data-testid={`hiring-tab-${k}`}>
            {l}
          </button>
        ))}
      </div>

      {(tab === "shortlist" || tab === "all") && (
        <CandidateTable
          candidates={candidates}
          scope={tab === "shortlist" ? "shortlist" : "all"}
          onCopyLink={copyLink}
          onInspect={setInspectId}
          onGenerateFit={generateOneFit}
          genOne={genOne}
          wsId={activeWorkspaceId}
          aid={aid}
          onReload={load}
        />
      )}

      {tab === "questions" && (
        <div className="space-y-3" data-testid="hiring-questions-tab">
          <div className="border p-4 space-y-2">
            <div className="font-medium">Question delivery</div>
            <div className="text-xs text-muted-foreground">Randomise the question order for every attendee so no two candidates see the exact same sequence, and manage the underlying question bank right here.</div>
            <QuizShuffleToggle assessment={assessment} wsId={activeWorkspaceId} onSaved={load} />
          </div>
          <QuestionBankPreview assessment={assessment} wsId={activeWorkspaceId} defaultExpanded />
        </div>
      )}

      {tab === "analytics" && (
        <AnalyticsPanel aid={aid} wsId={activeWorkspaceId} />
      )}

      {tab === "settings" && (
        <SettingsPanel assessment={assessment} onSaveCutoff={saveCutoff} onReload={load} activeWorkspaceId={activeWorkspaceId} />
      )}

      {/* Inspect drawer */}
      <Dialog open={!!inspect} onOpenChange={(v) => !v && setInspectId(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          {inspect && (
            <>
              <DialogHeader>
                <DialogTitle>{inspect.name || "Candidate"} — Rank #{inspect.rank}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3 border p-3">
                  <Field label="Email" value={inspect.email} />
                  {CAPTURE_FIELDS.map(cf => inspect[cf.key] && (
                    <Field key={cf.key} label={cf.label} value={
                      (cf.key === "linkedin_url" || cf.key === "resume_url") ? (
                        <a href={inspect[cf.key]} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{inspect[cf.key]}</a>
                      ) : inspect[cf.key]
                    } />
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 border p-3 text-center">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Score</div>
                    <div className={`text-2xl font-mono ${inspect.cutoff_passed ? "text-emerald-600" : "text-rose-600"}`}>{inspect.score_percent}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Violations</div>
                    <div className="text-2xl font-mono">{inspect.violations_count}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">AI Fit</div>
                    <div className="text-2xl font-mono">{inspect.ai_fit_score ?? "—"}</div>
                  </div>
                </div>
                {inspect.ai_fit_summary && (
                  <div className="border p-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">AI verdict</div>
                    <p className="text-sm">{inspect.ai_fit_summary}</p>
                  </div>
                )}
                {inspect.violations?.length > 0 && (
                  <div className="border p-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Violation log</div>
                    <ul className="text-xs space-y-1">
                      {inspect.violations.slice(0, 10).map((v, i) => (
                        <li key={i} className="text-rose-600">• {v.kind} at {new Date(v.at).toLocaleTimeString()}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <ResponsesSection candidateId={inspect.id} wsId={activeWorkspaceId} />
                <AttemptHistorySection
                  email={inspect.email}
                  currentId={inspect.id}
                  candidates={candidates}
                  onSwitch={(id) => setInspectId(id)}
                />
              </div>
              <DialogFooter>
                {!inspect.ai_fit_summary && (
                  <Button className="rounded-none" onClick={() => generateOneFit(inspect)} disabled={genOne[inspect.id]}>
                    {genOne[inspect.id] ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    Generate AI fit summary
                  </Button>
                )}
                <Button variant="outline" className="rounded-none" onClick={() => setInspectId(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// R157c · Feb 2026 — Candidate table used by both the Ranked shortlist
// (passed-only, default) and the All attempts tab (every submission,
// with status filter chips). Extracted from the inline table so the
// same UI can be reused with different filters + empty states.
// R157d · Feb 2026 — Added bulk-select, bulk actions (shortlist / reject /
// clear / email / export selected), manual_status chips, filter chip
// for manual_status, and header-level Export All CSV button.
function CandidateTable({ candidates, scope = "shortlist", onCopyLink, onInspect, onGenerateFit, genOne, wsId, aid, onReload }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const filtered = candidates.filter((c) => {
    if (scope === "shortlist") return !!c.cutoff_passed;
    if (statusFilter === "all") return true;
    if (statusFilter === "passed") return !!c.cutoff_passed;
    if (statusFilter === "failed") return !c.cutoff_passed;
    if (statusFilter === "auto") return !!c.auto_submitted;
    if (statusFilter === "flagged") return (c.violations_count || 0) >= 3;
    if (statusFilter === "shortlisted") return c.manual_status === "shortlisted";
    if (statusFilter === "rejected") return c.manual_status === "rejected";
    return true;
  });

  const chips = [
    { v: "all", label: `All (${candidates.length})` },
    { v: "passed", label: `Passed (${candidates.filter(c => c.cutoff_passed).length})` },
    { v: "failed", label: `Below cutoff (${candidates.filter(c => !c.cutoff_passed).length})` },
    { v: "shortlisted", label: `Shortlisted (${candidates.filter(c => c.manual_status === "shortlisted").length})` },
    { v: "rejected", label: `Rejected (${candidates.filter(c => c.manual_status === "rejected").length})` },
    { v: "auto", label: `Auto-submitted (${candidates.filter(c => c.auto_submitted).length})` },
    { v: "flagged", label: `Flagged (${candidates.filter(c => (c.violations_count || 0) >= 3).length})` },
  ];

  // Reset selection when filter changes / candidates change.
  useEffect(() => { setSelected(new Set()); }, [statusFilter, scope, candidates.length]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };
  const toggleAll = () => {
    if (filtered.every((c) => selected.has(c.id)) && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((c) => c.id)));
    }
  };

  const selectedRows = filtered.filter((c) => selected.has(c.id));

  const bulkSetStatus = async (manualStatus) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/hiring/assessments/${aid}/bulk-status`, {
        ids: Array.from(selected),
        manual_status: manualStatus,
      });
      toast.success(`${data.updated} candidate${data.updated === 1 ? "" : "s"} ${manualStatus || "cleared"}`);
      setSelected(new Set());
      onReload && onReload();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk action failed")); }
    finally { setBusy(false); }
  };

  const exportCsv = (idsScope) => {
    // idsScope: "all" (current tab filter) or "selected"
    const ids = idsScope === "selected" ? Array.from(selected).join(",") : "";
    const base = `${api.defaults.baseURL || ""}/workspaces/${wsId}/hiring/assessments/${aid}/responses.csv`;
    const url = ids ? `${base}?ids=${encodeURIComponent(ids)}` : base;
    // Send auth via a link click (browser attaches cookie) — but our API
    // uses Bearer token. Fetch then download to preserve auth.
    (async () => {
      try {
        const resp = await api.get(url.replace(api.defaults.baseURL || "", ""), { responseType: "blob" });
        const blob = new Blob([resp.data], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        const cd = resp.headers["content-disposition"] || "";
        const match = /filename="?([^"]+)"?/i.exec(cd);
        a.download = match ? match[1] : "candidates.csv";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
        toast.success("CSV downloaded");
      } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
    })();
  };

  if (candidates.length === 0) {
    return (
      <div className="border">
        <div className="p-8 text-center text-sm text-muted-foreground">
          No candidates yet. Share the public link with candidates to start collecting submissions.
          <div className="mt-2">
            <Button size="sm" className="rounded-none" onClick={onCopyLink}>
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy public link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`hiring-candidate-table-${scope}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {scope === "all" && (
          <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0" data-testid="hiring-all-filter-chips">
            {chips.map((c) => (
              <button
                key={c.v}
                onClick={() => setStatusFilter(c.v)}
                className={`text-xs px-2.5 py-1 border ${statusFilter === c.v ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-medium" : "border-neutral-200 text-neutral-600 hover:border-neutral-400"}`}
                data-testid={`hiring-filter-${c.v}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
        {scope === "shortlist" && (
          <div className="text-[11px] text-muted-foreground flex-1">
            Showing candidates who passed the cutoff score. Select rows to bulk-email or export the top performers, or open <b>All attempts</b> for below-cutoff and rejected submissions.
          </div>
        )}
        <Button
          size="sm"
          variant="outline"
          className="rounded-none h-8 text-xs"
          onClick={() => exportCsv("all")}
          data-testid="hiring-export-all-csv"
          title="Download a CSV of every candidate on this tab"
        >
          <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Bulk actions bar — enabled on BOTH shortlist and all tabs (R157e) */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-2 flex-wrap" data-testid="hiring-bulk-bar">
          <span className="text-xs font-medium text-emerald-800">
            {selected.size} selected
          </span>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => bulkSetStatus("shortlisted")} disabled={busy} data-testid="hiring-bulk-shortlist">
            <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" /> Shortlist
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => bulkSetStatus("rejected")} disabled={busy} data-testid="hiring-bulk-reject">
            <Ban className="w-3 h-3 mr-1 text-rose-600" /> Reject
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => bulkSetStatus(null)} disabled={busy} data-testid="hiring-bulk-clear">
            <XCircle className="w-3 h-3 mr-1" /> Clear status
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setEmailOpen(true)} disabled={busy || selectedRows.every(r => !r.email)} data-testid="hiring-bulk-email">
            <Mail className="w-3 h-3 mr-1" /> Email selected
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => exportCsv("selected")} disabled={busy} data-testid="hiring-bulk-export">
            <Download className="w-3 h-3 mr-1" /> Export selected
          </Button>
          <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs ml-auto" onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </div>
      )}

      <div className="border">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground italic">No candidates match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="text-left p-2 w-8">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every((c) => selected.has(c.id))}
                      ref={(el) => { if (el) el.indeterminate = selected.size > 0 && !filtered.every((c) => selected.has(c.id)); }}
                      onChange={toggleAll}
                      data-testid="hiring-select-all"
                    />
                  </th>
                  <th className="text-left p-2 w-10">#</th>
                  <th className="text-left p-2">Candidate</th>
                  <th className="text-left p-2">Score</th>
                  <th className="text-left p-2">State</th>
                  <th className="text-left p-2">AI Fit</th>
                  <th className="text-left p-2">Submitted</th>
                  <th className="text-right p-2"> </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} className={`border-t hover:bg-neutral-50/60 ${selected.has(c.id) ? "bg-emerald-50/40" : ""}`} data-testid={`hiring-row-${c.id}`}>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                        data-testid={`hiring-select-${c.id}`}
                      />
                    </td>
                    <td className="p-2 font-mono">
                      {c.rank === 1 ? <Trophy className="w-4 h-4 text-amber-500 inline" /> : c.rank}
                    </td>
                    <td className="p-2">
                      <div className="font-medium flex items-center gap-1.5 flex-wrap">
                        {c.name || "—"}
                        {c.manual_status === "shortlisted" && (
                          <span className="text-[9px] uppercase tracking-widest bg-emerald-100 text-emerald-700 border border-emerald-300 px-1 py-0.5">shortlisted</span>
                        )}
                        {c.manual_status === "rejected" && (
                          <span className="text-[9px] uppercase tracking-widest bg-rose-100 text-rose-700 border border-rose-300 px-1 py-0.5">rejected</span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{c.email}</div>
                    </td>
                    <td className="p-2">
                      <div className={`font-mono font-semibold ${c.cutoff_passed ? "text-emerald-600" : "text-rose-600"}`}>{c.score_percent}%</div>
                      <div className="text-[10px]">{c.cutoff_passed ? "PASSED" : "below"}</div>
                    </td>
                    <td className="p-2">
                      {c.violations_count > 0 ? (
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 border ${c.violations_count >= 3 ? "border-rose-400 text-rose-600 bg-rose-50" : "border-amber-400 text-amber-600 bg-amber-50"}`}>
                          <AlertTriangle className="w-3 h-3" /> {c.violations_count}
                          {c.auto_submitted && <span className="ml-1">· auto</span>}
                        </span>
                      ) : c.auto_submitted ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border border-amber-400 text-amber-600 bg-amber-50">auto-submitted</span>
                      ) : (
                        <span className="text-xs text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="p-2 max-w-[280px]">
                      {c.ai_fit_summary ? (
                        <>
                          <div className="text-[11px] text-neutral-700 line-clamp-2">{c.ai_fit_summary}</div>
                          {c.ai_fit_score !== null && c.ai_fit_score !== undefined && (
                            <div className="text-[10px] font-mono text-neutral-500 mt-0.5">Fit score: <b className={c.ai_fit_score >= 70 ? "text-emerald-600" : c.ai_fit_score >= 40 ? "text-amber-600" : "text-rose-600"}>{c.ai_fit_score}</b></div>
                          )}
                        </>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]" onClick={() => onGenerateFit(c)} disabled={genOne[c.id]} data-testid={`hiring-fit-${c.id}`}>
                          {genOne[c.id] ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                          Generate
                        </Button>
                      )}
                    </td>
                    <td className="p-2 text-[11px] text-muted-foreground">
                      {c.submitted_at ? new Date(c.submitted_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => onInspect(c.id)} data-testid={`hiring-inspect-${c.id}`}>
                        Inspect
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {emailOpen && (
        <BulkEmailDialog
          recipients={selectedRows.filter((r) => r.email)}
          wsId={wsId}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </div>
  );
}


function Kpi({ icon, label, value }) {
  return (
    <div className="border p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500">{icon}{label}</div>
      <div className="text-2xl font-mono mt-1">{value}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

function SettingsPanel({ assessment, onSaveCutoff, onReload, activeWorkspaceId }) {
  const [cutoff, setCutoff] = useState(assessment.cutoff_score);
  const [captureCfg, setCaptureCfg] = useState(assessment.capture_config || {});
  const [saving, setSaving] = useState(false);

  const toggle = (key, prop) => {
    setCaptureCfg(c => {
      const next = { ...c, [key]: { ...c[key], [prop]: !c[key]?.[prop] } };
      if (prop === "enabled" && !next[key].enabled) next[key].required = false;
      if (prop === "required" && next[key].required) next[key].enabled = true;
      return next;
    });
  };

  const saveCapture = async () => {
    setSaving(true);
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/hiring/assessments/${assessment.id}`, { capture_config: captureCfg });
      toast.success("Capture config saved");
      onReload();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border p-4 space-y-2">
        <div className="font-medium">Cutoff score</div>
        <div className="text-xs text-muted-foreground">Candidates scoring at or above this threshold are marked PASSED and ranked first.</div>
        <div className="flex items-center gap-2">
          <Input type="number" value={cutoff} onChange={e => setCutoff(e.target.value)} className="h-9 w-24 rounded-none" data-testid="hiring-cutoff-input" />
          <span>%</span>
          <Button className="rounded-none" size="sm" onClick={() => onSaveCutoff(cutoff)} data-testid="hiring-cutoff-save">
            <Save className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
        </div>
      </div>

      <div className="border p-4 space-y-2">
        <div className="font-medium">Candidate capture fields</div>
        <div className="text-xs text-muted-foreground">Configure what each candidate must fill on the public form. Name + email always captured.</div>
        <div className="space-y-1 pt-2">
          {CAPTURE_FIELDS.map(cf => (
            <div key={cf.key} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-xs border-b py-1.5">
              <div className="font-medium">{cf.label}</div>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={!!captureCfg[cf.key]?.enabled} onChange={() => toggle(cf.key, "enabled")} data-testid={`hiring-set-${cf.key}-en`} />
                Ask
              </label>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={!!captureCfg[cf.key]?.required} onChange={() => toggle(cf.key, "required")} data-testid={`hiring-set-${cf.key}-req`} />
                Required
              </label>
            </div>
          ))}
        </div>
        <Button className="rounded-none" size="sm" onClick={saveCapture} disabled={saving} data-testid="hiring-capture-save">
          <Save className="w-3.5 h-3.5 mr-1" /> Save capture config
        </Button>
      </div>

      <div className="border p-4 space-y-2" data-testid="hiring-quiz-settings">
        <div className="font-medium">Question delivery</div>
        <div className="text-xs text-muted-foreground">Randomise the question order for every attendee so no two candidates see the exact same sequence. Manage the underlying question bank from the <b>Questions</b> tab.</div>
        <QuizShuffleToggle assessment={assessment} wsId={activeWorkspaceId} onSaved={onReload} />
      </div>

      <div className="border p-4 space-y-2">
        <div className="font-medium">Job description</div>
        <div className="whitespace-pre-wrap text-sm text-neutral-600 bg-neutral-50 p-3 border max-h-[300px] overflow-auto">{assessment.jd_text}</div>
      </div>
    </div>
  );
}


// R156 · Feb 2026 — Shuffle-questions toggle wired to the underlying
// quiz (assessments.quiz_id → lms_quizzes.shuffle_questions).
function QuizShuffleToggle({ assessment, wsId, onSaved }) {
  const qid = assessment.quiz_id;
  const [quiz, setQuiz] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!qid || !wsId) return;
    api.get(`/workspaces/${wsId}/lms/quizzes/${qid}`).then(({ data }) => setQuiz(data)).catch(() => setQuiz(null));
  }, [qid, wsId]);
  if (!qid) return <div className="text-[11px] text-muted-foreground italic">No quiz linked to this assessment yet.</div>;
  if (!quiz) return <div className="text-[11px] text-muted-foreground">Loading…</div>;
  const flip = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${wsId}/lms/quizzes/${qid}`, { shuffle_questions: !quiz.shuffle_questions });
      setQuiz({ ...quiz, shuffle_questions: !quiz.shuffle_questions });
      toast.success(!quiz.shuffle_questions ? "Randomised per attendee" : "Fixed order");
      onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <label className="flex items-center gap-2 text-xs pt-1">
      <input type="checkbox" checked={!!quiz.shuffle_questions} onChange={flip} disabled={busy} data-testid="hiring-quiz-shuffle" />
      Randomise question order per attendee
    </label>
  );
}

// R156 · Feb 2026 — Question bank preview + add-custom drawer for the
// underlying quiz. Shows every question in the quiz + optional custom
// entries the recruiter adds.
function QuestionBankPreview({ assessment, wsId, defaultExpanded = false }) {
  const qid = assessment.quiz_id;
  const [open, setOpen] = useState(!!defaultExpanded);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newQ, setNewQ] = useState({ type: "mcq", text: "", options: ["", "", "", ""], correct: "A", points: 1 });

  const load = async () => {
    if (!qid || !wsId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/lms/quizzes/${qid}/questions`);
      setRows(Array.isArray(data) ? data : (data?.rows || data?.questions || []));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load questions")); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, qid, wsId]);

  const addQ = async () => {
    if (!newQ.text.trim()) { toast.error("Question text required"); return; }
    try {
      const t = newQ.type === "short_answer" ? "short_text" : newQ.type;
      const body = {
        type: t,
        text: newQ.text.trim(),
        points: Number(newQ.points) || 1,
      };
      if (t === "mcq" || t === "multi") {
        body.options = newQ.options.map((label, i) => ({ key: String.fromCharCode(65 + i), label: label || "" })).filter((o) => o.label);
        body.correct_answers = [newQ.correct];
      } else if (t === "truefalse") {
        body.options = [{ key: "T", label: "True" }, { key: "F", label: "False" }];
        body.correct_answers = [newQ.correct === "A" ? "T" : newQ.correct];
      }
      await api.post(`/workspaces/${wsId}/lms/quizzes/${qid}/questions`, body);
      toast.success("Question added");
      setShowAdd(false);
      setNewQ({ type: "mcq", text: "", options: ["", "", "", ""], correct: "A", points: 1 });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };

  if (!qid) return null;
  return (
    <div className="pt-2">
      {!defaultExpanded && (
        <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1" data-testid="hiring-bank-toggle">
          {open ? "Hide" : "Preview"} question bank {rows.length ? `(${rows.length})` : ""}
        </button>
      )}
      {open && (
        <div className="mt-2 border border-border" data-testid="hiring-bank-preview">
          <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{rows.length} question{rows.length === 1 ? "" : "s"}</span>
            <Button size="sm" variant="outline" className="rounded-none h-7 ml-auto" onClick={() => setShowAdd(true)} data-testid="hiring-bank-add-btn">
              + Add custom
            </Button>
          </div>
          {busy ? <div className="p-4 text-xs text-muted-foreground">Loading…</div> : (
            <ol className="divide-y divide-border max-h-[420px] overflow-y-auto">
              {rows.map((q, i) => (
                <li key={q.id || i} className="p-3 space-y-1 text-xs" data-testid={`hiring-bank-q-${q.id || i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-muted-foreground">#{i + 1}</span>
                    <span className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5">{q.type}</span>
                    <span className="text-[10px] text-muted-foreground">{q.points || 1} pt{(q.points || 1) === 1 ? "" : "s"}</span>
                  </div>
                  <div className="whitespace-pre-wrap font-medium">{q.text || q.question || ""}</div>
                  {(q.options || []).length > 0 && (
                    <ul className="mt-1 ml-2 space-y-0.5">
                      {q.options.map((o) => {
                        const isCorrect = Array.isArray(q.correct_answers) ? q.correct_answers.includes(o.key) : q.correct === o.key;
                        return (
                          <li key={o.key} className={`text-[11px] ${isCorrect ? "text-emerald-700 font-semibold" : "text-muted-foreground"}`}>
                            <span className="font-mono mr-1">{o.key}.</span> {o.label}{isCorrect ? " ✓" : ""}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              ))}
              {rows.length === 0 && <li className="p-4 text-xs text-muted-foreground text-center italic">No questions yet.</li>}
            </ol>
          )}
        </div>
      )}
      {showAdd && (
        <Dialog open onOpenChange={(v) => !v && setShowAdd(false)}>
          <DialogContent className="max-w-lg" data-testid="hiring-bank-add-dialog">
            <DialogHeader><DialogTitle>Add a custom question</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Type</Label>
                <select value={newQ.type} onChange={(e) => setNewQ({ ...newQ, type: e.target.value })} className="w-full h-9 border border-border bg-background px-2 text-sm" data-testid="hiring-bank-add-type">
                  <option value="mcq">Single-choice</option>
                  <option value="multi">Multi-choice</option>
                  <option value="truefalse">True / False</option>
                  <option value="short_answer">Short answer</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Question *</Label>
                <textarea value={newQ.text} onChange={(e) => setNewQ({ ...newQ, text: e.target.value })} rows={3} className="w-full text-sm border border-border bg-background px-2 py-1 resize-y" data-testid="hiring-bank-add-question" />
              </div>
              {(newQ.type === "mcq" || newQ.type === "multi") && (
                <div className="space-y-1">
                  {newQ.options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="font-mono text-xs w-4">{String.fromCharCode(65 + i)}.</span>
                      <Input value={opt} onChange={(e) => { const n = [...newQ.options]; n[i] = e.target.value; setNewQ({ ...newQ, options: n }); }} className="h-8 text-xs" data-testid={`hiring-bank-add-opt-${i}`} />
                      {newQ.type === "mcq" && (
                        <label className="text-xs flex items-center gap-1">
                          <input type="radio" name="correct" checked={newQ.correct === String.fromCharCode(65 + i)} onChange={() => setNewQ({ ...newQ, correct: String.fromCharCode(65 + i) })} /> ✓
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label className="text-xs">Points</Label>
                <Input type="number" min="1" value={newQ.points} onChange={(e) => setNewQ({ ...newQ, points: e.target.value })} className="h-8 text-sm w-24" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-none" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button className="rounded-none" onClick={addQ} data-testid="hiring-bank-add-save">Add question</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}



// R156 · Feb 2026 — Per-candidate responses drilldown inside the Inspect
// drawer. Joins attempt.per_question with each question's text/options.

// R157d · Feb 2026 — Bulk email dialog. Two modes:
//   1. "Write fresh" — recruiter types a subject + body once; each
//      selected candidate receives an individual email (to = their
//      address) so nobody sees each other's names.
//   2. "Use email template" — pick a template from the Comms module;
//      each candidate receives a rendered copy with their own name,
//      email, score substituted via the /email-templates/{tid}/render
//      helper.
function BulkEmailDialog({ recipients, wsId, onClose }) {
  const [mode, setMode] = useState("fresh"); // 'fresh' | 'template'
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // for template mode

  useEffect(() => {
    api.get(`/workspaces/${wsId}/email-templates`, { params: { status: "active" } })
      .then(({ data }) => setTemplates(data.rows || []))
      .catch(() => {});
  }, [wsId]);

  const chosen = templates.find((t) => t.id === tplId);

  const renderForCandidate = async (candidate) => {
    // Ask the backend to render the picked template with this
    // candidate's variables. Returns { subject, body_html }.
    const { data } = await api.post(`/email-templates/${tplId}/render`, {
      data: {
        candidate: {
          name: candidate.name || "",
          email: candidate.email || "",
          score: candidate.score_percent,
          passed: candidate.cutoff_passed ? "PASSED" : "BELOW CUTOFF",
          manual_status: candidate.manual_status || "",
        },
      },
    });
    return { subject: data.subject, html: data.body_html };
  };

  useEffect(() => {
    // Live-preview the first recipient in template mode.
    if (mode !== "template" || !tplId || recipients.length === 0) { setPreview(null); return; }
    let cancelled = false;
    renderForCandidate(recipients[0])
      .then((r) => { if (!cancelled) setPreview(r); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tplId, recipients.length]);

  const send = async () => {
    setBusy(true);
    let sent = 0, failed = 0;
    try {
      for (const c of recipients) {
        try {
          let s, h;
          if (mode === "template" && tplId) {
            const r = await renderForCandidate(c);
            s = r.subject; h = r.html;
          } else {
            s = subject; h = bodyHtml;
          }
          if (!s || !h || h === "<p></p>") throw new Error("Empty subject or body");
          await api.post(`/workspaces/${wsId}/mail/send`, {
            to: c.email, subject: s, html: h,
          });
          if (mode === "template" && tplId) {
            // best-effort log for template analytics
            api.post(`/email-templates/${tplId}/track-send`, {
              recipient: c.email, entity_type: "candidate", entity_id: c.id,
            }).catch(() => {});
          }
          sent += 1;
        } catch (e) {
          console.warn("Bulk email failed for", c.email, e);
          failed += 1;
        }
      }
      if (failed === 0) toast.success(`Sent ${sent} email${sent === 1 ? "" : "s"}`);
      else if (sent === 0) toast.error(`All ${failed} sends failed`);
      else toast.warning(`Sent ${sent} · ${failed} failed`);
      if (sent > 0) onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hiring-bulk-email-dialog">
        <DialogHeader>
          <DialogTitle>Email {recipients.length} candidate{recipients.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Mode picker */}
          <div className="flex gap-1 border-b">
            {[["fresh", "Write fresh"], ["template", "Use template"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setMode(k)}
                className={`px-3 py-2 text-sm border-b-2 ${mode === k ? "border-emerald-500 text-emerald-600" : "border-transparent text-neutral-600 hover:text-neutral-900"}`}
                data-testid={`hiring-bulk-email-mode-${k}`}
              >
                {l}
              </button>
            ))}
          </div>

          {mode === "fresh" ? (
            <>
              <div>
                <Label className="text-xs">Subject *</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Your assessment result — {{candidate.name}}"
                  className="mt-1"
                  data-testid="hiring-bulk-email-subject"
                />
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  Same subject and body will be sent to every selected candidate.
                </div>
              </div>
              <div>
                <Label className="text-xs">Body (HTML) *</Label>
                <textarea
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={8}
                  placeholder="<p>Hi there,</p><p>Thank you for taking our assessment. …</p>"
                  className="w-full text-sm border border-border bg-background px-2 py-1 font-mono resize-y mt-1"
                  data-testid="hiring-bulk-email-body"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">Template *</Label>
                <select
                  value={tplId}
                  onChange={(e) => setTplId(e.target.value)}
                  className="w-full text-sm border border-border bg-background px-2 py-1 mt-1"
                  data-testid="hiring-bulk-email-template-picker"
                >
                  <option value="">— Choose a template —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}{t.category ? ` · ${t.category}` : ""}</option>
                  ))}
                </select>
                {templates.length === 0 && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    No email templates found. Create one under <b>Comms → Templates</b> first.
                  </div>
                )}
              </div>
              {chosen && preview && (
                <div className="border p-3 bg-neutral-50">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    Preview (rendered for {recipients[0].email})
                  </div>
                  <div className="text-sm font-medium mb-1">{preview.subject}</div>
                  <div className="text-xs bg-background border p-2 max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: preview.html }} />
                </div>
              )}
            </>
          )}

          {/* Recipient chip list */}
          <div>
            <Label className="text-xs">Recipients</Label>
            <div className="flex flex-wrap gap-1 mt-1 max-h-24 overflow-y-auto" data-testid="hiring-bulk-email-recipients">
              {recipients.map((r) => (
                <span key={r.id} className="text-[11px] px-1.5 py-0.5 border bg-background font-mono">
                  {r.email}
                </span>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} className="rounded-none">Cancel</Button>
          <Button
            onClick={send}
            disabled={busy || (mode === "fresh" ? (!subject.trim() || !bodyHtml.trim()) : !tplId)}
            className="rounded-none"
            data-testid="hiring-bulk-email-send"
          >
            {busy ? "Sending…" : `Send ${recipients.length} email${recipients.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// R157c · Feb 2026 — Show every OTHER attempt by the same email on
// this assessment so the recruiter can see multi-attempt history at a
// glance (someone who retried a link, or who was invited twice).
function AttemptHistorySection({ email, currentId, candidates, onSwitch }) {
  if (!email) return null;
  const others = (candidates || []).filter(
    (c) => c.email && c.email.toLowerCase() === email.toLowerCase() && c.id !== currentId,
  );
  if (others.length === 0) return null;
  const sorted = [...others].sort((a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || ""));
  return (
    <div className="border p-3" data-testid="hiring-inspect-history">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
        Attempt history for {email} ({sorted.length} other attempt{sorted.length === 1 ? "" : "s"})
      </div>
      <ul className="divide-y">
        {sorted.map((c) => (
          <li key={c.id} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`hiring-history-${c.id}`}>
            <span className={`font-mono font-semibold w-14 ${c.cutoff_passed ? "text-emerald-600" : "text-rose-600"}`}>{c.score_percent}%</span>
            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${c.cutoff_passed ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-rose-300 text-rose-700 bg-rose-50"}`}>
              {c.cutoff_passed ? "pass" : "fail"}
            </span>
            {c.auto_submitted && <span className="text-[9px] uppercase tracking-widest text-amber-600 border border-amber-300 bg-amber-50 px-1 py-0.5">auto</span>}
            {c.violations_count > 0 && <span className="text-[9px] uppercase tracking-widest text-rose-600 border border-rose-300 bg-rose-50 px-1 py-0.5">{c.violations_count} viol</span>}
            <span className="text-muted-foreground flex-1 truncate">{c.submitted_at ? new Date(c.submitted_at).toLocaleString() : "—"}</span>
            <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px]" onClick={() => onSwitch(c.id)} data-testid={`hiring-history-open-${c.id}`}>
              Open
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}


function ResponsesSection({ candidateId, wsId }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!candidateId || !wsId) return;
    setBusy(true); setErr(null);
    api.get(`/workspaces/${wsId}/hiring/candidates/${candidateId}/responses`)
      .then(({ data }) => setRows(data.responses || []))
      .catch((e) => setErr(apiErrorMessage(e, "Failed to load responses")))
      .finally(() => setBusy(false));
  }, [candidateId, wsId]);

  const downloadCsv = async () => {
    try {
      const url = `/workspaces/${wsId}/hiring/candidates/${candidateId}/responses.csv`;
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = resp.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      a.download = match ? match[1] : "responses.csv";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
      toast.success("Detailed CSV downloaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
  };

  if (busy) return <div className="text-xs text-muted-foreground">Loading responses…</div>;
  if (err) return <div className="text-xs text-rose-600">{err}</div>;
  if (!rows) return null;
  if (rows.length === 0) return (
    <div className="border" data-testid="hiring-inspect-responses">
      <div className="px-3 py-2 bg-neutral-50 border-b flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Per-question responses</div>
        <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px]" onClick={downloadCsv} data-testid="hiring-inspect-export-csv" title="Download the candidate's identity + score summary as CSV">
          <Download className="w-3 h-3 mr-1" /> CSV
        </Button>
      </div>
      <div className="px-3 py-4 text-xs text-muted-foreground italic">No per-question responses recorded. The candidate summary is still downloadable as CSV.</div>
    </div>
  );

  const renderGiven = (r) => {
    const given = r.given;
    if (given == null || given === "") return <span className="text-neutral-400">— no answer —</span>;
    if (Array.isArray(given)) return given.join(", ");
    if (typeof given === "object") return JSON.stringify(given);
    return String(given);
  };

  return (
    <div className="border" data-testid="hiring-inspect-responses">
      <div className="px-3 py-2 bg-neutral-50 border-b flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Per-question responses ({rows.length})</div>
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-muted-foreground">
            {rows.filter(r => r.correct).length} correct · {rows.filter(r => !r.correct).length} wrong
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-none h-6 text-[10px]"
            onClick={downloadCsv}
            data-testid="hiring-inspect-export-csv"
            title="Download detailed CSV with every question, given answer, and correct answer"
          >
            <Download className="w-3 h-3 mr-1" /> CSV
          </Button>
        </div>
      </div>
      <ol className="divide-y max-h-[420px] overflow-y-auto">
        {rows.map((r) => (
          <li key={r.question_id || r.order} className="p-3 space-y-1 text-xs" data-testid={`hiring-resp-${r.question_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-muted-foreground">#{r.order}</span>
              <span className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5">{r.type}</span>
              {r.section && <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{r.section}</span>}
              <span className={`text-[10px] font-mono ml-auto ${r.correct ? "text-emerald-600" : "text-rose-600"}`}>
                {r.correct ? "✓ correct" : "✗ wrong"} · {r.points_earned}/{r.points_possible} pts
              </span>
            </div>
            <div className="font-medium whitespace-pre-wrap">{r.text}</div>
            {(r.options || []).length > 0 && (
              <ul className="ml-2 space-y-0.5">
                {r.options.map((o) => {
                  const isCorrect = (r.correct_answers || []).includes(o.key);
                  const isGiven = Array.isArray(r.given) ? r.given.includes(o.key) : r.given === o.key;
                  return (
                    <li key={o.key} className={`text-[11px] ${isCorrect ? "text-emerald-700 font-semibold" : isGiven ? "text-rose-600" : "text-muted-foreground"}`}>
                      <span className="font-mono mr-1">{o.key}.</span> {o.label}
                      {isCorrect && " ✓"}
                      {isGiven && !isCorrect && " ← chosen"}
                    </li>
                  );
                })}
              </ul>
            )}
            {(!r.options || r.options.length === 0) && (
              <div className="text-[11px] text-neutral-700 bg-neutral-50 border p-2">
                <div className="text-[9px] uppercase text-neutral-500 tracking-widest mb-0.5">Candidate answer</div>
                {renderGiven(r)}
                {r.correct_text && (
                  <div className="mt-1 pt-1 border-t text-emerald-700">
                    <span className="text-[9px] uppercase tracking-widest text-neutral-500">Expected keyword:</span> {r.correct_text}
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

// R156 · Feb 2026 — Assessment analytics tab combining question-level
// accuracy and candidate distribution charts.
function AnalyticsPanel({ aid, wsId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    if (!aid || !wsId) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/hiring/assessments/${aid}/analytics`);
      setData(data);
    } catch (e) { setErr(apiErrorMessage(e, "Failed to load analytics")); }
    finally { setBusy(false); }
  }, [aid, wsId]);
  useEffect(() => { load(); }, [load]);

  if (busy) return <div className="p-6 text-sm text-muted-foreground">Loading analytics…</div>;
  if (err) return <div className="p-6 text-sm text-rose-600">{err}</div>;
  if (!data) return null;
  const cs = data.candidate_stats || {};
  const qs = data.question_stats || [];

  const fmtTime = (s) => {
    if (!s) return "—";
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}m ${sec}s`;
  };

  return (
    <div className="space-y-4" data-testid="hiring-analytics-panel">
      {/* Candidate stat cards */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1 mb-2"><BarChart3 className="w-3.5 h-3.5" /> Candidate performance</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={<Users className="w-4 h-4 text-neutral-600" />} label="Total submissions" value={cs.total || 0} />
          <Kpi icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" />} label="Pass rate" value={`${cs.pass_rate || 0}%`} />
          <Kpi icon={<Trophy className="w-4 h-4 text-amber-500" />} label="Avg / median / top" value={`${cs.avg_score || 0} / ${cs.median_score || 0} / ${cs.top_score || 0}`} />
          <Kpi icon={<RefreshCw className="w-4 h-4 text-neutral-600" />} label="Avg time taken" value={fmtTime(cs.avg_time_secs)} />
          <Kpi icon={<AlertTriangle className="w-4 h-4 text-amber-500" />} label="Auto-submitted" value={cs.auto_submitted || 0} />
          <Kpi icon={<AlertTriangle className="w-4 h-4 text-rose-500" />} label="Avg violations" value={cs.avg_violations || 0} />
          <Kpi icon={<XCircle className="w-4 h-4 text-rose-500" />} label="Failed" value={cs.failed || 0} />
          <Kpi icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" />} label="Passed" value={cs.passed || 0} />
        </div>
      </div>

      {/* Score distribution chart */}
      <div className="border p-4" data-testid="hiring-analytics-distribution">
        <div className="text-sm font-medium mb-2">Score distribution</div>
        <div className="w-full h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={cs.distribution || []} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.05)" }} />
              <Bar dataKey="count" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Submission trend */}
      {cs.trend && cs.trend.length > 0 && (
        <div className="border p-4" data-testid="hiring-analytics-trend">
          <div className="text-sm font-medium mb-2">Daily submissions</div>
          <div className="w-full h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cs.trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Question analytics */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1 mb-2"><ListChecks className="w-3.5 h-3.5" /> Question performance</div>
        <div className="border overflow-hidden" data-testid="hiring-analytics-questions">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="text-left p-2 w-10">#</th>
                  <th className="text-left p-2">Question</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Section</th>
                  <th className="text-right p-2">Attempts</th>
                  <th className="text-right p-2">Accuracy</th>
                  <th className="text-right p-2">Pts ratio</th>
                </tr>
              </thead>
              <tbody>
                {qs.map((q, i) => (
                  <tr key={q.question_id || i} className="border-t hover:bg-neutral-50/60">
                    <td className="p-2 font-mono text-xs">{i + 1}</td>
                    <td className="p-2 text-xs max-w-[420px] truncate" title={q.text}>{q.text}</td>
                    <td className="p-2 text-[10px] uppercase tracking-widest text-neutral-500">{q.type}</td>
                    <td className="p-2 text-[10px] text-neutral-500">{q.section}</td>
                    <td className="p-2 text-right font-mono">{q.attempts}</td>
                    <td className="p-2 text-right font-mono">
                      {q.accuracy_percent == null ? "—" : (
                        <span className={q.accuracy_percent >= 70 ? "text-emerald-600" : q.accuracy_percent >= 40 ? "text-amber-600" : "text-rose-600"}>
                          {q.accuracy_percent}%
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-right font-mono">{q.points_ratio == null ? "—" : `${q.points_ratio}%`}</td>
                  </tr>
                ))}
                {qs.length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-xs text-muted-foreground">No submitted attempts yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* R157e · Feb 2026 — Manual status audit trail */}
      <AuditTrailSection aid={aid} wsId={wsId} />
    </div>
  );
}

// R157e · Feb 2026 — Manual-status audit trail. Reads activity_log
// entries with action ∈ {hiring.shortlisted, hiring.rejected,
// hiring.manual_status_cleared} scoped to this assessment so hiring
// managers can trace who decided what and when.
function AuditTrailSection({ aid, wsId }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!aid || !wsId) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/hiring/assessments/${aid}/audit`);
      setRows(data.rows || []);
    } catch (e) { setErr(apiErrorMessage(e, "Failed to load audit trail")); }
    finally { setBusy(false); }
  }, [aid, wsId]);
  useEffect(() => { load(); }, [load]);

  const actionMeta = (a) => {
    if (a === "hiring.shortlisted") return { label: "Shortlisted", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" };
    if (a === "hiring.rejected") return { label: "Rejected", cls: "bg-rose-100 text-rose-700 border-rose-300" };
    return { label: "Cleared", cls: "bg-neutral-100 text-neutral-600 border-neutral-300" };
  };

  return (
    <div className="border" data-testid="hiring-audit-trail">
      <div className="px-4 py-2 bg-neutral-50 border-b flex items-center justify-between">
        <div className="text-sm font-medium">Manual status audit trail</div>
        <div className="flex items-center gap-2">
          {rows && rows.length > 0 && <span className="text-[10px] text-muted-foreground">{rows.length} event{rows.length === 1 ? "" : "s"}</span>}
          <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px]" onClick={load} disabled={busy} title="Refresh" data-testid="hiring-audit-refresh">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      {busy && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
      {err && <div className="p-4 text-xs text-rose-600">{err}</div>}
      {!busy && !err && rows && rows.length === 0 && (
        <div className="p-4 text-xs text-muted-foreground italic">No shortlist / reject actions yet. As you use the bulk-status buttons, each decision is recorded here with the recruiter&apos;s name and timestamp.</div>
      )}
      {rows && rows.length > 0 && (
        <ol className="divide-y max-h-[420px] overflow-y-auto">
          {rows.map((r) => {
            const m = actionMeta(r.action);
            const meta = r.metadata || {};
            return (
              <li key={r.id} className="px-4 py-2 flex items-start gap-3 text-xs" data-testid={`hiring-audit-row-${r.id}`}>
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.target_label}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {meta.email || ""}{meta.score_percent !== undefined && meta.score_percent !== null ? ` · ${meta.score_percent}%` : ""}
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground text-right whitespace-nowrap">
                  <div>by <b>{r.actor_name}</b></div>
                  <div>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

